import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { PortTypeFilters, PortTypes } from '../models';
import * as portTypesService from '../services/port-types.service';

export const usePortTypes = (defaultFilters: Partial<PortTypeFilters & { searchParams: { ids: string[] } }> = {}) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<PortTypeFilters>>(otherDefaultFilters);

  const cacheData = portTypesService.getAll();

  const updateFilters = useCallback((newFilters: Partial<PortTypeFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const { data, isLoading, isError } = useQuery(portTypesService.portTypesQueryOptions(filters));

  // Check if requested IDs are already in main data to prevent duplicate requests
  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length || !data?.map) {
      return [];
    }
    return searchParams.ids.filter((id) => !data?.map[id] && !cacheData?.map[id]);
  }, [searchParams?.ids, data?.map, cacheData?.map]);

  const {
    data: preloadData,
    isLoading: isPreloadLoading,
    isError: isPreloadError,
  } = useQuery(
    portTypesService.portTypesQueryOptions(
      { search: { id: missingIds.join(',') } },
      { enabled: missingIds.length > 0 },
    ),
  );

  const mergedMap: PortTypes['map'] = useMemo(
    () => ({
      ...(data?.map ?? {}),
      ...(cacheData?.map ?? {}),
      ...(preloadData?.map ?? {}),
    }),
    [data?.map, cacheData?.map, preloadData?.map],
  );

  const list = useMemo(() => Object.values(mergedMap), [mergedMap]);

  return {
    data: {
      list,
      map: mergedMap,
    },
    isError: isError || isPreloadError,
    isLoading: isLoading || isPreloadLoading,
    updateFilters,
  };
};
